import { defineStore } from "pinia";
import { useNotificationsStore } from "@/stores/notifications";
import { useApiBlocksStore } from "@/stores/apiBlocks";
import {
  ISmartMergeTag,
  IMergeTag,
  IMergeTagArray,
  MergeTagsArraySchema,
  SmartMergeTagsSchema,
} from "@/schemas/mergeTags/mergeTagsSchema";
import mergeTagsToLinkList, {
  type LinkListItem,
} from "@/utils/mergeTagsToLinkList";
import { parseLoopMergeTagValue } from "@/utils/parseLoopMergeTagValue";

interface State {
  mergeTags: IMergeTagArray;
  smartMergeTags: ISmartMergeTag;
  mergeTagsValues: string[];
  showMergePreviewPanel: boolean;
  showAllToPreview: boolean;
  enableMergeTagPreview?: boolean;
  isNotAtHome: boolean;
  languageMergeTagName: string;
  languageMergeTag: IMergeTag | null;
  hostMTSyntax: ISmartMergeTag["syntax"] | null;
}

export type { ISmartMergeTag };

export const useMergeTagsStore = defineStore("mergeTags", {
  state: (): State => ({
    mergeTags: [
      {
        name: "My Merge Tags",
        items: [
          {
            value: "*|FIRST_NAME|*",
            text: "First Name",
            label: "Name",
          },
        ],
      },
    ],
    smartMergeTags: {
      enabled: false,
      patterns: {},
      syntax: {
        start: "*|",
        end: "|*",
      },
    },
    mergeTagsValues: [],
    showMergePreviewPanel: false,
    showAllToPreview: false,
    enableMergeTagPreview: false,
    isNotAtHome: false,
    languageMergeTagName: "TOPOL_TEMPLATE_LANG",
    languageMergeTag: null,
    hostMTSyntax: null,
  }),

  getters: {
    mergeTagsWithoutArray(): IMergeTagArray {
      const mergeTagsFiltered = filterArrayFromMergeTags(
        JSON.parse(JSON.stringify(this.mergeTags))
      );
      return mergeTagsFiltered;
    },

    mergeTagLinkList(): LinkListItem[] {
      return mergeTagsToLinkList(this.mergeTagsWithoutArray);
    },

    mergeTagsForInputs(): IMergeTagArray {
      const filtered = filterForInputs(
        JSON.parse(JSON.stringify(this.mergeTags)),
        this.MTSyntax
      );
      return filtered;
    },

    flatMergeTags(): IMergeTag[] {
      const flatMergeTags: IMergeTag[] = getFlatMergeTags(
        this.mergeTags,
        this.MTSyntax
      );

      const filtered = flatMergeTags.filter((tag) => !tag.childrenProperties);

      if (
        this.languageMergeTag &&
        !filtered.find((tag) => tag.value === this.languageMergeTag!.value)
      ) {
        filtered.push(this.languageMergeTag);
      }

      return filtered;
    },

    flatMergeTagsWithArray(): IMergeTag[] {
      const syntax = this.MTSyntax;
      const flatMergeTags: IMergeTag[] = getFlatMergeTags(
        this.mergeTags,
        syntax
      );

      const apiBlocksStore = useApiBlocksStore();
      const synthesized = synthesizeApiBlockMergeTags(
        apiBlocksStore.blocks,
        syntax
      );
      synthesized.forEach((tag) => {
        const existing = flatMergeTags.find((m) => m.value === tag.value);
        if (!existing) {
          flatMergeTags.push(tag);
        } else if (!existing.childrenProperties?.length) {
          existing.childrenProperties = tag.childrenProperties;
        }
      });

      return flatMergeTags;
    },

    /**
     * The `smartMergeTags` option for a render request (the backend reads
     * `options.smartMergeTags.syntax`, the same shape as the init option), or
     * `undefined` when the host did not configure a usable syntax so nothing
     * is sent. An empty delimiter means the defaults, which the backend
     * applies on its own.
     */
    smartMergeTagsForRequest():
      | { syntax: NonNullable<ISmartMergeTag["syntax"]> }
      | undefined {
      // `undefined`, not `null`: JSON.stringify drops the key, whereas `null`
      // would go on the wire and fail the backend's optional() model.
      const syntax = this.hostMTSyntax;
      return syntax && syntax.start && syntax.end ? { syntax } : undefined;
    },

    // Normalized merge tags for BE. Loop tags (childrenProperties) go out
    // as arrays only when their JSON is valid; flat tags go out as strings.
    normalizedMergeTagsForPreview(): {
      value: string;
      data: string | Record<string, unknown>[];
    }[] {
      const mergeTags = this.flatMergeTagsWithArray;
      const mergeTagsValue = this.mergeTagsValues;

      const normalizedMergeTags: {
        value: string;
        data: string | Record<string, unknown>[];
      }[] = [];

      mergeTags.forEach((mergeTag, i) => {
        const raw = mergeTagsValue[i];
        if (mergeTag.childrenProperties?.length) {
          const parsed = parseLoopMergeTagValue(raw);
          if (parsed.status === "ok") {
            normalizedMergeTags.push({
              value: mergeTag.value,
              data: parsed.data,
            });
          }
          return;
        }
        if (raw) {
          const trimmed = raw.trim();
          // A JSON array is never a flat value; string-replacing it would
          // splice unescaped quotes into the serialised template.
          if (trimmed.startsWith("[") && trimmed.endsWith("]")) return;
          normalizedMergeTags.push({ value: mergeTag.value, data: raw });
        }
      });

      return normalizedMergeTags;
    },

    MTRegex(): string {
      const syntax = this.MTSyntax;
      const startMtRegex = "\\" + syntax.start.split("").join("\\");
      const endMtRegex = "\\" + syntax.end.split("").join("\\");

      return `${startMtRegex}([a-zA-Z_0-9\\.\\[\\]]+)${endMtRegex}`;
    },

    MTSyntax(): {
      start: string;
      end: string;
    } {
      return (
        this.smartMergeTags?.syntax || {
          start: "*|",
          end: "|*",
        }
      );
    },
  },

  actions: {
    setSmartMergeTags(smartMergeTags: ISmartMergeTag) {
      const { error, data, success } =
        SmartMergeTagsSchema.safeParse(smartMergeTags);
      if (!success) {
        console.error("Error while setting smart merge tags", error.issues);
        const notificationsStore = useNotificationsStore();
        notificationsStore.createNotification({
          text: "Error while setting smart merge tags",
          type: "error",
        });
        return;
      }
      this.smartMergeTags = { ...this.smartMergeTags, ...data };
      if (data.syntax) this.hostMTSyntax = data.syntax;
    },
    setEnableMergeTagPreview(val: boolean) {
      this.enableMergeTagPreview = val;
    },
    setValueOfMergetag(value: string, index: number) {
      this.mergeTagsValues[index] = value;
    },
    setLanguageMergeTagString(name: string) {
      this.languageMergeTagName = name;
    },

    updateLanguageMergeTag(languageCodes: string[]) {
      if (languageCodes.length < 2) {
        this.languageMergeTag = null;
        return;
      }

      const syntax = this.smartMergeTags?.syntax || {
        start: "*|",
        end: "|*",
      };

      this.languageMergeTag = {
        value: `${syntax.start}${this.languageMergeTagName}${syntax.end}`,
        text: this.languageMergeTagName,
        label: "Template Language",
        suggestions: languageCodes,
      };
    },

    setMergeTags(mergeTags: IMergeTagArray) {
      const { error, data, success } =
        MergeTagsArraySchema.safeParse(mergeTags);

      if (!success) {
        console.error("Error while setting merge tags", error.issues);
        const notificationsStore = useNotificationsStore();
        notificationsStore.createNotification({
          text: "Error while setting merge tags",
          type: "error",
        });
        return;
      }

      this.mergeTags = data;

      this.flatMergeTagsWithArray.forEach((mergeTag, i) => {
        if (mergeTag.defaultValue) {
          this.mergeTagsValues[i] = mergeTag.defaultValue;
        }
      });
    },
  },
});

function getFlatMergeTags(
  mergeTags: IMergeTagArray,
  syntax: { start: string; end: string },
  parentPath?: string
): IMergeTag[] {
  const flatMergeTags: IMergeTag[] = [];

  if (!Array.isArray(mergeTags)) return [];

  mergeTags.forEach((mergeTag) => {
    if ("items" in mergeTag) {
      const newPath = parentPath
        ? `${parentPath} > ${mergeTag.name}`
        : mergeTag.name;
      flatMergeTags.push(...getFlatMergeTags(mergeTag.items, syntax, newPath));
    } else {
      if (
        mergeTagAlreadyExists(flatMergeTags, mergeTag) ||
        !isMergeTagValid(mergeTag, { start: syntax.start, end: syntax.end })
      )
        return;
      flatMergeTags.push({ ...mergeTag, parentPath });
    }
  });

  return flatMergeTags;
}

function mergeTagAlreadyExists(mergeTags: IMergeTag[], mergeTag: IMergeTag) {
  return mergeTags.find((m) => m.value === mergeTag.value);
}

function isMergeTagValid(
  mergeTag: IMergeTag,
  syntax: { start: string; end: string }
) {
  const mergeTagsSyntaxStartEscaped = "^\\" + syntax.start.split("").join("\\");
  const mergeTagsSyntaxEndEscaped = "\\" + syntax.end.split("").join("\\");

  const mergeTagScannerRegex =
    mergeTagsSyntaxStartEscaped +
    "[a-zA-Z_0-9.[\\]]*" +
    mergeTagsSyntaxEndEscaped;

  const mergeTagRegex = new RegExp(mergeTagScannerRegex, "g");
  return mergeTagRegex.test(mergeTag.value);
}

function filterArrayFromMergeTags(mergeTags: IMergeTagArray): IMergeTagArray {
  return mergeTags.filter((mergeTag) => {
    if ("items" in mergeTag) {
      mergeTag.items = filterArrayFromMergeTags(mergeTag.items);
      return mergeTag.items.length > 0;
    }
    return !mergeTag.childrenProperties;
  });
}

function filterForInputs(
  mergeTags: IMergeTagArray,
  syntax: { start: string; end: string }
): IMergeTagArray {
  return mergeTags.filter((mergeTag) => {
    if ("items" in mergeTag) {
      mergeTag.items = filterForInputs(mergeTag.items, syntax);
      return mergeTag.items.length > 0;
    }
    // Exclude array-type merge tags
    if (mergeTag.childrenProperties) return false;
    // Exclude HTML snippets — only keep pure merge tag values
    if (/<[a-zA-Z][\s\S]*>/.test(mergeTag.value)) return false;
    // Ensure the value matches merge tag syntax
    return isMergeTagValid(mergeTag, syntax);
  });
}

function synthesizeApiBlockMergeTags(
  blocks: Map<string, any>,
  syntax: { start: string; end: string }
): IMergeTag[] {
  const result: IMergeTag[] = [];
  const seen = new Set<string>();

  blocks.forEach((block) => {
    if (!block?.dynamicMergetags?.length || !block.blockStructure) return;

    const apiStructure = block.apiStructure || {};
    const childrenProperties = Object.keys(block.blockStructure)
      .filter((key) => key !== "layout" && key !== "button")
      .map((key) => {
        // Mirror the alias resolution in mjmlStore.addApiBlockDynamicItem so
        // dummy data and the merge-tag picker emit the same API-shaped field
        // names that get inserted when the user picks a feed item.
        let placeholderKey: string;
        if (key === "price-before" && apiStructure.price_before) {
          placeholderKey = apiStructure.price_before;
        } else if (key === "price" && apiStructure.price_vat) {
          placeholderKey = apiStructure.price_vat;
        } else {
          placeholderKey = apiStructure[key] || key;
        }
        const value = `${syntax.start}${placeholderKey}${syntax.end}`;
        const label = block.blockStructure[key]?.label || key;
        let type: "image" | "button" | "text" = "text";
        if (key === "image") type = "image";
        else if (key === "href") type = "button";
        return { value, text: label, label, type };
      });

    if (childrenProperties.length === 0) return;

    block.dynamicMergetags.forEach((mt: { value: string; label: string }) => {
      if (seen.has(mt.value)) return;
      seen.add(mt.value);
      result.push({
        value: mt.value,
        text: mt.label,
        label: mt.label,
        childrenProperties,
      });
    });
  });

  return result;
}
